import { useQuery } from '@apollo/client/react';
import RestoreIcon from '@mui/icons-material/Restore';
import { Button, Grid, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { titleize } from 'inflection';
import { find } from 'lodash';
import { map as mapFp, debounce } from 'lodash/fp';
import React, { useContext, useRef } from 'react';

import { type TasksFiltersType } from '.';

import FilterAutocomplete from '@/components/FilterAutocomplete';
import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  TaskStatusEnum,
  TaskPriorityEnum,
  type Project,
  type Report,
  type Event,
  GetPreloadTasksDrawerFiltersDataDocument,
} from '@/graphql/generated/graphql';
import { ENTITY_TYPE } from '@/utils/constants';

const normalDebounce = debounce(500);

interface TasksFiltersProps {
  filters: TasksFiltersType;
  onChange: (key: keyof TasksFiltersType, value: TasksFiltersType[keyof TasksFiltersType]) => void;
  onReset: () => void;
  disableResetButton: boolean;
}

const mapNode = mapFp('node');

const ProjectsFilters: React.FC<TasksFiltersProps> = ({ filters, onChange, onReset, disableResetButton }) => {
  const { currentCompany, dateTimePreference } = useContext(CurrentUserContext);
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;

  const { data: preloadData } = useQuery(GetPreloadTasksDrawerFiltersDataDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !currentCompany?.id,
    variables: { companyId: currentCompany?.id as string },
  });

  const handleSearchTextChange = (key: keyof TasksFiltersType, value: string) => {
    debounceFunc.cancel();
    debounceFunc(() => {
      onChange(key, value);
    });
  };

  const getEntityOptions = () => {
    if (filters.entity.type === 'Project') {
      return mapNode(preloadData?.getProjects?.edges);
    }
    if (filters.entity.type === 'Report') {
      return mapNode(preloadData?.getCompanyReports?.edges);
    }
    if (filters.entity.type === 'Event') {
      return preloadData?.getCompanyEvents;
    }

    return [];
  };

  const getEntityOptionLabel = (option: Project | Report | Event) => {
    if (option.__typename === 'Report') {
      return `${option.project.code} - ${dayjs(option.reportDate).format(dateTimePreference.date)}`;
    }

    if (option.__typename === 'Project' || option.__typename === 'Event') {
      return option.title;
    }

    return '';
  };

  return (
    <Grid container spacing={2} sx={{ my: 1 }} size={12}>
      <Grid size={3}>
        <FilterAutocomplete
          textFieldLabel="Created By"
          options={mapNode(preloadData?.getEmployees?.edges) || []}
          onChange={(_, value) => {
            onChange('createdById', value ? value.companyEmployment.id : '');
          }}
          getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
          value={
            find(mapNode(preloadData?.getEmployees?.edges) || [], (employee) => employee.id === filters.createdById) ||
            ''
          }
        />
      </Grid>
      <Grid size={3}>
        <FilterAutocomplete
          textFieldLabel="Assigned To"
          options={mapNode(preloadData?.getEmployees?.edges) || []}
          onChange={(_, value) => {
            onChange('assigneeId', value ? value.companyEmployment.id : '');
          }}
          getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
          value={
            find(mapNode(preloadData?.getEmployees?.edges) || [], (employee) => employee.id === filters.assigneeId) ||
            ''
          }
        />
      </Grid>
      <Grid size={3}>
        <FilterAutocomplete
          textFieldLabel="Status"
          options={Object.values(TaskStatusEnum)}
          onChange={(_, value) => {
            onChange('status', value);
          }}
          getOptionLabel={(option) => titleize(option)}
        />
      </Grid>
      <Grid size={3}>
        <FilterAutocomplete
          textFieldLabel="Priority"
          options={Object.values(TaskPriorityEnum)}
          onChange={(_, value) => {
            onChange('priority', value);
          }}
          getOptionLabel={(option) => titleize(option)}
        />
      </Grid>
      <Grid size={3}>
        <TextField
          fullWidth
          size="small"
          defaultValue={filters.searchText}
          onChange={(e) => handleSearchTextChange('searchText', e.target.value)}
          placeholder="Search by Description..."
        />
      </Grid>
      <Grid size={3}>
        <FilterAutocomplete
          textFieldLabel="Entity Type"
          options={ENTITY_TYPE}
          onChange={(_, value) => {
            onChange('entity', { type: value, id: '' });
          }}
        />
      </Grid>
      {filters.entity.type && (
        <Grid size={3}>
          <FilterAutocomplete
            textFieldLabel={`Select ${filters.entity.type}`}
            options={getEntityOptions() || []}
            value={find(getEntityOptions(), (ent) => ent.id == filters.entity.id) || ''}
            getOptionLabel={(option) => (option ? getEntityOptionLabel(option) : '')}
            onChange={(_, value) => {
              onChange('entity', { type: filters.entity.type, id: value.id });
            }}
          />
        </Grid>
      )}
      <Grid container size={6} flexWrap="nowrap">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Due Date From"
            value={filters.dueDateFrom ? dayjs(filters.dueDateFrom) : null}
            onChange={(date) => onChange('dueDateFrom', date)}
            format={dateTimePreference.date}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
          <DatePicker
            label="Due Date To"
            value={filters.dueDateTo ? dayjs(filters.dueDateTo) : null}
            onChange={(date) => onChange('dueDateTo', date)}
            format={dateTimePreference.date}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid container alignItems="center" justifyContent="flex-start" size={2}>
        <Button
          variant="outlined"
          onClick={onReset}
          fullWidth
          startIcon={<RestoreIcon />}
          size="small"
          disabled={disableResetButton}
        >
          Reset
        </Button>
      </Grid>
    </Grid>
  );
};

export default ProjectsFilters;
